import React, { useEffect, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

export type TransformStep = 0 | 1 | 2 | 3;

type LabelKey = 'app-1' | 'app-2' | 'app-3' | 'org-1' | 'org-2' | 'org-3';

type StepCopy = {
  tab: string;
  title: string;
  text: string;
  labels: Record<LabelKey, string>;
};

export const TRANSFORM_STEPS: StepCopy[] = [
  {
    tab: 'Before',
    title: 'Before Luminafactory',
    text: 'Chat wrappers. Shadow tools. No owner. Your product waits while the platform gets built.',
    labels: {
      'app-1': 'Waiting on a platform team',
      'app-2': 'Stuck in a prototype chat',
      'app-3': 'No path to production',
      'org-1': 'Shipping without AI they own',
      'org-2': 'Shadow tools, no budget owner',
      'org-3': 'No view of spend or risk',
    },
  },
  {
    tab: 'Your product',
    title: 'Your applications go live',
    text: 'Agents show up inside the products you already ship — with budgets, keys, and a sandbox first.',
    labels: {
      'app-1': 'Live agent in the product',
      'app-2': 'Sandboxed, then published',
      'app-3': 'First production call today',
      'org-1': 'Still waiting for access',
      'org-2': 'Still waiting for access',
      'org-3': 'Still waiting for access',
    },
  },
  {
    tab: 'Your org',
    title: 'Your organisation can use them',
    text: 'Teams get AI they are allowed to use. Your data and tools attach on your terms.',
    labels: {
      'app-1': 'Live agent in the product',
      'app-2': 'Sandboxed, then published',
      'app-3': 'First production call today',
      'org-1': 'Building on the same agents',
      'org-2': 'Using AI they are allowed to',
      'org-3': 'Spend and risk in one place',
    },
  },
  {
    tab: 'Live',
    title: 'Minutes, not a rebuild',
    text: 'Sign up. Wire an application. Let people in. The factory was already running.',
    labels: {
      'app-1': 'Live in the customer journey',
      'app-2': 'Live for the people who run the work',
      'app-3': 'Live for partners, under your keys',
      'org-1': 'Shipping AI they own',
      'org-2': 'One toolkit, attached on purpose',
      'org-3': 'Usage they can actually see',
    },
  },
];

const APP_NODES: { key: LabelKey; name: string }[] = [
  { key: 'app-1', name: 'Customer app' },
  { key: 'app-2', name: 'Internal ops' },
  { key: 'app-3', name: 'Partner portal' },
];

const ORG_NODES: { key: LabelKey; name: string }[] = [
  { key: 'org-1', name: 'Product' },
  { key: 'org-2', name: 'Operations' },
  { key: 'org-3', name: 'Leadership' },
];

const STEP_MS = 3800;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(45, 154, 143, 0.45); }
  100% { box-shadow: 0 0 0 10px rgba(45, 154, 143, 0); }
`;

const Scene = styled.section`
  margin-top: 1.75rem;
  width: 100%;
  text-align: left;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 1.15rem 1.1rem 1rem;
  box-shadow: 0 12px 40px rgba(26, 35, 50, 0.12);
  color: #1a2332;
`;

const Tabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  justify-content: center;
  margin-bottom: 1rem;
`;

const Tab = styled.button<{ $active: boolean }>`
  border: 1px solid ${({ $active }) => ($active ? '#1a2332' : '#e5e7eb')};
  background: ${({ $active }) => ($active ? '#1a2332' : '#fff')};
  color: ${({ $active }) => ($active ? '#fff' : '#475569')};
  border-radius: 999px;
  padding: 0.4rem 0.8rem;
  font-weight: 600;
  font-size: 0.75rem;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.2s, color 0.2s, border-color 0.2s;

  &:hover {
    background: #1a2332;
    border-color: #1a2332;
    color: #fff;
  }
`;

const Board = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.85rem;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const Column = styled.div`
  min-width: 0;
`;

const ColumnTitle = styled.h3`
  font-size: 0.7rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
  margin: 0 0 0.55rem;
  font-weight: 700;
`;

const Node = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.55rem 0.7rem;
  margin-bottom: 0.45rem;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #f8fafc;
  transition: transform 0.4s ease, border-color 0.4s, background 0.4s, box-shadow 0.4s;

  strong {
    display: block;
    font-size: 0.8125rem;
    font-weight: 650;
    color: #1a2332;
  }

  span {
    display: block;
    font-size: 0.7rem;
    color: #64748b;
    line-height: 1.35;
  }
`;

const NodeDot = styled.i`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #94a3b8;
  flex-shrink: 0;
  display: block;
`;

const SceneLit = styled(Scene)<{ $step: TransformStep }>`
  ${({ $step }) =>
    ($step === 1 || $step === 3) &&
    css`
      [data-track='apps'] ${Node} {
        background: #e8f7f6;
        border-color: #2d9a8f;
        box-shadow: 0 0 0 4px rgba(45, 154, 143, 0.12);
      }
      [data-track='apps'] ${NodeDot} {
        background: #2d9a8f;
        box-shadow: 0 0 0 4px rgba(45, 154, 143, 0.25);
        animation: ${pulse} 1.6s ease-out infinite;
      }
    `}

  ${({ $step }) =>
    ($step === 2 || $step === 3) &&
    css`
      [data-track='org'] ${Node} {
        background: #fff7ed;
        border-color: #f97316;
        box-shadow: 0 0 0 4px rgba(249, 115, 22, 0.12);
      }
      [data-track='org'] ${NodeDot} {
        background: #f97316;
        animation: ${pulse} 1.6s ease-out infinite;
      }
    `}

  ${({ $step }) =>
    $step === 3 &&
    css`
      ${Node} {
        transform: translateY(-2px);
      }
    `}

  @media (prefers-reduced-motion: reduce) {
    ${Node}, ${NodeDot} {
      animation: none !important;
      transition: none !important;
    }
  }
`;

const Caption = styled.p`
  margin: 0.9rem 0 0;
  text-align: center;
  min-height: 3.4rem;

  strong {
    display: block;
    font-size: 0.9375rem;
    color: #1a2332;
    margin-bottom: 0.25rem;
  }

  span {
    color: #475569;
    font-size: 0.75rem;
    line-height: 1.45;
  }
`;

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

const TransformScene: React.FC = () => {
  const [step, setStep] = useState<TransformStep>(0);
  const [paused, setPaused] = useState(false);
  const copy = TRANSFORM_STEPS[step];

  useEffect(() => {
    if (paused || prefersReducedMotion()) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      setStep((current) => ((current + 1) % TRANSFORM_STEPS.length) as TransformStep);
    }, STEP_MS);
    return () => window.clearInterval(timer);
  }, [paused]);

  return (
    <SceneLit
      $step={step}
      data-active={step}
      aria-label="Transformation from prototype to live"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      <Tabs role="tablist" aria-label="Transformation steps">
        {TRANSFORM_STEPS.map((item, index) => {
          const active = step === index;
          return (
            <Tab
              key={item.tab}
              type="button"
              role="tab"
              $active={active}
              aria-selected={active}
              onClick={() => setStep(index as TransformStep)}
            >
              {item.tab}
            </Tab>
          );
        })}
      </Tabs>
      <Board>
        <Column data-track="apps">
          <ColumnTitle>Applications</ColumnTitle>
          {APP_NODES.map((node) => (
            <Node key={node.key}>
              <NodeDot />
              <div>
                <strong>{node.name}</strong>
                <span>{copy.labels[node.key]}</span>
              </div>
            </Node>
          ))}
        </Column>
        <Column data-track="org">
          <ColumnTitle>Organisation</ColumnTitle>
          {ORG_NODES.map((node) => (
            <Node key={node.key}>
              <NodeDot />
              <div>
                <strong>{node.name}</strong>
                <span>{copy.labels[node.key]}</span>
              </div>
            </Node>
          ))}
        </Column>
      </Board>
      <Caption>
        <strong>{copy.title}</strong>
        <span>{copy.text}</span>
      </Caption>
    </SceneLit>
  );
};

export default TransformScene;
